import { useCallback, useEffect, useState } from 'react';
import { jobTrackerApi } from '../services/jobTrackerApi';

/**
 * Bridges Job Search and the Job Tracker board.
 *
 * A search result is linked to at most one tracker card through `sourceId`
 * (the listing slug), so bookmarking and applying always act on the same card
 * rather than piling up duplicates — the server enforces this too.
 *
 * The tracker is treated as optional here: if any of these calls fail, Job
 * Search must keep working, so errors surface through `error` and never throw
 * out of the hook.
 */

const SAVED_JOBS_KEY = 'mrs_saved_jobs';
const MIGRATION_FLAG = 'mrs_saved_jobs_seeded';
const MIGRATION_CAP = 25;

/* Search results report free-text job types; the tracker stores an enum. */
const JOB_TYPE_ALIASES = {
  'fulltime': 'full-time', 'full-time': 'full-time', 'full time': 'full-time',
  'parttime': 'part-time', 'part-time': 'part-time', 'part time': 'part-time',
  'contract': 'contract', 'contractor': 'contract', 'contracts': 'contract',
  'internship': 'internship', 'intern': 'internship',
  'temporary': 'temporary', 'temp': 'temporary',
  'remote': 'remote',
};

const toJobType = (job) => {
  for (const raw of job.job_types || []) {
    const key = String(raw).trim().toLowerCase();
    if (JOB_TYPE_ALIASES[key]) return JOB_TYPE_ALIASES[key];
  }
  return job.remote ? 'remote' : '';
};

/** Map a Job Search result onto the tracker's create payload. */
export const trackerPayload = (job, extra = {}) => ({
  title:    String(job.title || 'Untitled role').slice(0, 200),
  company:  String(job.company_name || 'Unknown company').slice(0, 200),
  location: String(job.location || '').slice(0, 200),
  url:      String(job.url || '').slice(0, 2000),
  salary:   String(job.salary || '').slice(0, 100),
  jobType:  toJobType(job),
  sourceId: String(job.slug || ''),
  source:   String(job.source || 'job_search'),
  stage:    'bookmarked',
  ...extra,
});

export const useTrackedJobs = () => {
  const [cards, setCards]     = useState({});    // sourceId -> tracker card
  const [busy, setBusy]       = useState({});    // sourceId -> in flight
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  /* Job Tracker is Pro-only. On a free plan the API answers 403; that is a
     plan boundary rather than a fault, so it hides the controls silently
     instead of showing an error over Job Search. */
  const [locked, setLocked]   = useState(false);

  const indexBySource = (list) => Object.fromEntries(
    (list || []).filter(c => c.sourceId).map(c => [c.sourceId, c]),
  );

  const setCard = (sourceId, card) => setCards(prev => ({ ...prev, [sourceId]: card }));
  const dropCard = (sourceId) => setCards((prev) => {
    const next = { ...prev }; delete next[sourceId]; return next;
  });
  const withBusy = (sourceId, value) => setBusy(prev => ({ ...prev, [sourceId]: value }));

  /* One-time seed: anything already in the local "Saved Jobs" list becomes a
     Bookmarked card. The local list is left untouched — that tab stays its own
     thing. Runs once per browser and is skipped entirely if it has run. */
  const seedFromSavedJobs = useCallback(async (existing) => {
    if (localStorage.getItem(MIGRATION_FLAG)) return null;
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(SAVED_JOBS_KEY) || '[]'); } catch { saved = []; }

    const tracked = indexBySource(existing);
    const pending = saved.filter(j => j?.slug && !tracked[j.slug]).slice(0, MIGRATION_CAP);
    if (!pending.length) { localStorage.setItem(MIGRATION_FLAG, '1'); return null; }

    const created = [];
    for (const job of pending) {
      try { created.push(await jobTrackerApi.create(trackerPayload(job))); }
      catch { /* keep going; a partial seed is better than none */ }
    }
    localStorage.setItem(MIGRATION_FLAG, '1');
    return created.length ? created : null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await jobTrackerApi.list();
        if (cancelled) return;
        setCards(indexBySource(list));
        const seeded = await seedFromSavedJobs(list);
        if (seeded && !cancelled) setCards(indexBySource([...list, ...seeded]));
      } catch (e) {
        if (cancelled) return;
        if (e.status === 403 || e.upgradeRequired) setLocked(true);
        else setError(e.message || 'Could not load your tracked jobs.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [seedFromSavedJobs]);

  const cardFor = useCallback((job) => (job?.slug ? cards[job.slug] : undefined), [cards]);

  /** Add to the board's Bookmarked column. */
  const bookmark = useCallback(async (job) => {
    const id = job?.slug;
    if (!id) return null;
    withBusy(id, true);
    setError('');
    try {
      const card = await jobTrackerApi.create(trackerPayload(job));
      setCard(id, card);
      return card;
    } catch (e) {
      setError(e.message || 'Could not add that job to your tracker.');
      return null;
    } finally { withBusy(id, false); }
  }, []);

  /** Remove the card this listing created. */
  const untrack = useCallback(async (job) => {
    const id = job?.slug;
    const card = id && cards[id];
    if (!card) return;
    withBusy(id, true);
    setError('');
    try {
      await jobTrackerApi.remove(card._id);
      dropCard(id);
    } catch (e) {
      setError(e.message || 'Could not remove that job from your tracker.');
    } finally { withBusy(id, false); }
  }, [cards]);

  /**
   * Move to Applied, creating the card first if the listing was never tracked.
   * Returns an undo snapshot so the caller can offer a way back — we cannot
   * verify the user really applied on the external site.
   */
  const markApplied = useCallback(async (job) => {
    const id = job?.slug;
    if (!id) return null;
    const existing = cards[id];
    if (existing && existing.stage === 'applied') return null;   // nothing to say

    withBusy(id, true);
    setError('');
    try {
      const appliedDate = new Date().toISOString();
      let card;
      let undo;
      if (existing) {
        card = await jobTrackerApi.update(existing._id, { stage: 'applied', appliedDate });
        undo = { kind: 'stage', cardId: existing._id, sourceId: id, stage: existing.stage, appliedDate: existing.appliedDate || '' };
      } else {
        card = await jobTrackerApi.create(trackerPayload(job, { stage: 'applied', appliedDate }));
        undo = { kind: 'created', cardId: card._id, sourceId: id };
      }
      setCard(id, card);
      return { card, undo };
    } catch (e) {
      setError(e.message || 'Could not update your tracker.');
      return null;
    } finally { withBusy(id, false); }
  }, [cards]);

  /** Reverse the last markApplied. */
  const undoApplied = useCallback(async (snapshot) => {
    if (!snapshot) return;
    setError('');
    try {
      if (snapshot.kind === 'created') {
        await jobTrackerApi.remove(snapshot.cardId);
        dropCard(snapshot.sourceId);
      } else {
        const restored = await jobTrackerApi.update(snapshot.cardId, {
          stage: snapshot.stage,
          appliedDate: snapshot.appliedDate || '',
        });
        setCard(snapshot.sourceId, restored);
      }
    } catch (e) {
      setError(e.message || 'Could not undo that change.');
    }
  }, []);

  return {
    loading, error, setError, locked,
    cardFor,
    isBusy: (job) => !!(job?.slug && busy[job.slug]),
    bookmark, untrack, markApplied, undoApplied,
    trackedCount: Object.keys(cards).length,
  };
};
